import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';
import { upsellLogger } from '@hotjar/webapp/domains/revenue/utils';
import {
  DatadogActionType,
  DatadogUpsellType,
} from '@hotjar/webapp/domains/revenue/upsell/constants';

interface Props {
  shouldRenderPostTrialModal: boolean;
}

/**
 * This hook is meant to send a "modal triggered" DataDog Log when the component will be renderer
 */
export const usePostTrialLogging = ({ shouldRenderPostTrialModal }: Props): void => {
  /**
   * Send the "triggered" log to Datadog, in both the happy-path(shouldRenderPostTrialModal = true)
   * and when there is an Error while fetching data(isLoadingError = true)
   *
   * The reason is that we need the "triggered" action in both cases(success and error) so that we can
   * accurately calculate the Post Trial Modal's error rate by using both the "triggered" and the "error" events.
   *
   * The <PostTrialErrorBoundary><PostTrialModalContainer/></PostTrialErrorBoundary>,
   * is always running, since it's auto-triggered inside the Overlays.tsx, without any interaction from the user.
   *
   * It's the same logic with other "auto-triggered" elements like "TrackingCodeModal", "FloatingSuggestions" or "TrackingCodeModal",
   * which are "global" modals and need to automatically triggered by the app. Then, it's up to the logic of these components
   * to decide whether it's going to render their presentational components, in this case the "PostTrialModal".
   */
  useEffectOnceWithDependencies(
    () => {
      upsellLogger?.info('Post Trial modal triggered', {
        upsellType: DatadogUpsellType.PostTrial,
        action: DatadogActionType.Trigger,
      });
    },
    [shouldRenderPostTrialModal],
    () => shouldRenderPostTrialModal,
  );
};
